import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import AdminSidebar from "components/common/AdminSidebar";
import Select from "components/common/SelectInput";
import Button from "components/common/Button";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import MobileContactSearch from "./mobile/MobileContactSearch";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 6 * 60 * 1000; // 6 minutes hard ceiling - a
// full 100-URL batch with several slow/dead sites can legitimately take a
// few minutes even with the backend's concurrency tuning; this just needs
// to outlast realistic worst-case runs, not be tight.
const MAX_URLS = 100;

const ContactSearch = () => {
  const { t } = useTranslation();

  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [formError, setFormError] = useState("");

  const [stage, setStage] = useState("form"); // 'form' | 'progress' | 'results'
  const [job, setJob] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pushMessage, setPushMessage] = useState("");
  const [discardMessage, setDiscardMessage] = useState("");
  const [progressError, setProgressError] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [pushSucceeded, setPushSucceeded] = useState(false);

  const pollTimer = useRef(null);
  const pollStartedAt = useRef(null);
  const pushInFlightRef = useRef(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const accessToken = await getValidToken();
        const response = await axios({
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/clients`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const options = response.data.clients.map((client) => ({
          label: `${client.Name} (ID: ${client.Id})`,
          value: client.Id,
        }));
        setClients(options);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const urlCount = urlsText.split("\n").filter((l) => l.trim().length > 0).length;

  const pollJob = useCallback((jobId) => {
    // Defensive: if a previous interval is somehow still alive (e.g. a
    // double-invocation), clear it first so we never end up with an
    // orphaned timer nobody can stop - overwriting pollTimer.current
    // without clearing the old one first would leak it forever.
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
    }
    pollStartedAt.current = Date.now();
    let consecutiveErrors = 0;

    pollTimer.current = setInterval(async () => {
      if (Date.now() - pollStartedAt.current > MAX_POLL_DURATION_MS) {
        clearInterval(pollTimer.current);
        setProgressError(t("admin.contactSearch.errorTimeout"));
        return;
      }

      try {
        const accessToken = await getValidToken();
        const response = await axios({
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs/${jobId}`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        consecutiveErrors = 0;
        setJob(response.data);

        if (response.data.status === "completed") {
          clearInterval(pollTimer.current);
          const successIds = response.data.results
            .filter((r) => r.is_successful)
            .map((r) => r.id);
          setSelectedIds(new Set(successIds));
          setStage("results");
        } else if (response.data.status === "failed") {
          clearInterval(pollTimer.current);
          setProgressError(t("admin.contactSearch.errorJobFailed"));
        }
      } catch (error) {
        // Tolerate an isolated transient blip (e.g. a momentary DB
        // hiccup) - only give up after several polls in a row fail.
        consecutiveErrors += 1;
        console.error("Error polling scrape job:", error);
        if (consecutiveErrors >= 3) {
          clearInterval(pollTimer.current);
          setProgressError(t("admin.contactSearch.errorTimeout"));
        }
      }
    }, POLL_INTERVAL_MS);
  }, [t]);

  const handleStart = async () => {
    const urls = urlsText.split("\n").map((u) => u.trim()).filter((u) => u.length > 0);

    if (!clientId) {
      setFormError(t("admin.contactSearch.errorNoClient"));
      return;
    }
    if (urls.length === 0) {
      setFormError(t("admin.contactSearch.errorNoUrls"));
      return;
    }
    setFormError("");
    setIsStarting(true);

    try {
      const accessToken = await getValidToken();
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs`,
        data: { ClientId: Number(clientId), Urls: urls.slice(0, MAX_URLS) },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setJob({ job_id: response.data.job_id, status: "running", total: response.data.total, processed: 0, results: [] });
      setProgressError("");
      setStage("progress");
      pollJob(response.data.job_id);
    } catch (error) {
      console.error("Error starting scrape job:", error);
      setFormError(t("admin.contactSearch.errorStart"));
    } finally {
      setIsStarting(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePush = async () => {
    // React's disabled={isPushing} prop doesn't update synchronously, so
    // two clicks fired within the same render frame could both slip
    // through before the button visually disables - this ref check is
    // instant and blocks that race outright.
    if (pushInFlightRef.current) return;
    pushInFlightRef.current = true;
    setIsPushing(true);
    setPushMessage("");
    try {
      const accessToken = await getValidToken();
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs/${job.job_id}/push`,
        data: { result_ids: Array.from(selectedIds) },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const ids = (response.data.pushed_items || []).map((c) => c.id);
      let message = t("admin.contactSearch.pushSuccess", { count: response.data.pushed_count });
      if (ids.length > 0) {
        message += ` ${t("admin.contactSearch.pushedIds")}: ${ids.join(", ")}.`;
      }
      if (response.data.failed_count > 0) {
        message += ` ${t("admin.contactSearch.pushPartialFailed", { count: response.data.failed_count })}`;
      }
      setPushMessage(message);
      setPushSucceeded(true);
    } catch (error) {
      console.error("Error pushing scrape results:", error);
      setPushMessage(t("admin.contactSearch.errorPush"));
      setPushSucceeded(false);
    } finally {
      setIsPushing(false);
      pushInFlightRef.current = false;
    }
  };

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      const accessToken = await getValidToken();
      await axios({
        method: "delete",
        url: `${process.env.REACT_APP_APIBASEURL}/api/admin/scrape-jobs/${job.job_id}`,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (error) {
      console.error("Error discarding scrape job:", error);
    } finally {
      setDiscardMessage(t("admin.contactSearch.discardSuccess"));
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const successResults = job?.results?.filter((r) => r.is_successful) || [];
  const failedResults = job?.results?.filter((r) => !r.is_successful) || [];
  const progressPct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <>
    <div
      className="hidden lg:flex min-h-screen"
      style={{
        background: "linear-gradient(-80deg, #051D42 20%, #0C469F 82%, #0D4AA8 100%)",
      }}
    >
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="text-[30px] font-bold text-white mb-2">
            {t("admin.contactSearch.head")}
          </h1>
          <p className="text-white mb-8 md:text-lg md:w-[735px]">
            {t("admin.contactSearch.desc")}
          </p>

          {stage === "form" && (
            <div className="bg-white rounded-xl p-6">
              <Select
                label={t("admin.option.selectClient")}
                labelClassName="text-primary"
                options={clients}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />

              <div className="mt-4">
                <label className="block mb-2 font-medium text-primary">
                  {t("admin.contactSearch.urlsLabel")}
                </label>
                <textarea
                  rows={8}
                  value={urlsText}
                  onChange={(e) => setUrlsText(e.target.value)}
                  placeholder="https://empresa1.com&#10;https://empresa2.com"
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-secondary"
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-500">
                    {urlCount} / {MAX_URLS} URLs
                  </span>
                  <Button
                    variant="primary"
                    onClick={handleStart}
                    disabled={isStarting}
                    className="!w-auto px-6 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isStarting ? (
                      <span className="flex items-center gap-2">
                        <img
                          src={logoIcon}
                          alt=""
                          className="w-4 h-4 animate-spin"
                        />
                        {t("admin.contactSearch.starting")}
                      </span>
                    ) : (
                      t("admin.contactSearch.start")
                    )}
                  </Button>
                </div>
                {formError && (
                  <p className="text-red-600 text-sm mt-2">{formError}</p>
                )}
              </div>
            </div>
          )}

          {stage === "progress" && job && (
            <div className="bg-white rounded-xl p-6">
              <p className="text-sm text-gray-600 mb-2">
                {t("admin.contactSearch.processing", { processed: job.processed, total: job.total })}
              </p>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-secondary transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {progressError && (
                <div className="mt-4">
                  <p className="text-red-600 text-sm mb-2">{progressError}</p>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setStage("form");
                      setJob(null);
                      setProgressError("");
                    }}
                    className="!w-auto px-6"
                  >
                    {t("admin.contactSearch.backToForm")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {stage === "results" && job && (
            <div className="flex flex-col gap-6">
              <div className="bg-white rounded-xl p-6">
                <p className="text-sm font-medium text-primary mb-3">
                  {t("admin.contactSearch.successful", { count: successResults.length })}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="py-2 pr-2"></th>
                        <th className="py-2 pr-2">{t("admin.contactSearch.colCompany")}</th>
                        <th className="py-2 pr-2">{t("admin.contactSearch.colPhone")}</th>
                        <th className="py-2 pr-2">{t("admin.contactSearch.colEmail")}</th>
                        <th className="py-2 pr-2">{t("admin.contactSearch.colCity")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {successResults.map((r) => (
                        <tr key={r.id} className="border-b text-primary">
                          <td className="py-2 pr-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleSelected(r.id)}
                            />
                          </td>
                          <td className="py-2 pr-2">{r.company}</td>
                          <td className="py-2 pr-2 font-mono">{r.phone}</td>
                          <td className="py-2 pr-2">{r.email}</td>
                          <td className="py-2 pr-2">{r.city}</td>
                        </tr>
                      ))}
                      {successResults.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-3 text-gray-400">
                            {t("admin.contactSearch.noneSuccessful")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6">
                <p className="text-sm font-medium text-primary mb-3">
                  {t("admin.contactSearch.failed", { count: failedResults.length })}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="py-2 pr-2">{t("admin.contactSearch.colUrl")}</th>
                        <th className="py-2 pr-2">{t("admin.contactSearch.colReason")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedResults.map((r) => (
                        <tr key={r.id} className="border-b">
                          <td className="py-2 pr-2 text-gray-600">{r.url}</td>
                          <td className="py-2 pr-2 text-red-600">{r.failure_reason}</td>
                        </tr>
                      ))}
                      {failedResults.length === 0 && (
                        <tr>
                          <td colSpan={2} className="py-3 text-gray-400">
                            {t("admin.contactSearch.noneFailed")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={handleDiscard}
                  disabled={isPushing || isDiscarding}
                  className="!w-auto px-6 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {t("admin.contactSearch.discard")}
                </Button>
                <Button
                  variant="primary"
                  onClick={handlePush}
                  disabled={isPushing || isDiscarding}
                  className="!w-auto px-6 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isPushing ? (
                    <span className="flex items-center gap-2">
                      <img src={logoIcon} alt="" className="w-4 h-4 animate-spin" />
                      {t("admin.contactSearch.pushing")}
                    </span>
                  ) : (
                    t("admin.contactSearch.push", { count: selectedIds.size })
                  )}
                </Button>
              </div>
            </div>
          )}

          {pushMessage && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
                <p className="text-primary text-base mb-4">{pushMessage}</p>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (pushSucceeded) {
                        // The job's already been deleted server-side on a
                        // successful push (same cleanup as Discard) - reload
                        // for a clean slate to paste the next batch.
                        window.location.reload();
                      } else {
                        setPushMessage("");
                      }
                    }}
                    className="!w-auto px-6"
                  >
                    {t("admin.contactSearch.pushOk")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {discardMessage && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
                <p className="text-primary text-base">{discardMessage}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    <div className="lg:hidden">
      <MobileContactSearch />
    </div>
    </>
  );
};

export default ContactSearch;
